import { useTheme } from "next-themes";
import { useLayoutEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { setIsComplexDesktop, setIsComplexPortrait, setIsReady, setPlayerParams } from "../../redux/slices/videoDataSlice";
import { conditions } from "../conditions/conditions";
import { ConditionName } from "../../interfaces/enums";
import { findIntersectionBetweenTwoArray, getUUID, handleIsComplexPortrait, staticFormats, strToObj } from "../helpers";
import { shallowEqual } from "react-redux";
import { IParams } from "interfaces";
import { allParams, isGeneralConditions, nonStaticConditions, quizConditions, rawConditions, staticConditions } from "../conditions/paramsConditions";
import Cookies from "js-cookie";

const useHandleParams = (query: any) => {
    const { setTheme } = useTheme();
    const dispatch = useAppDispatch();
    const { token, params } = useAppSelector(({ videoData: { token, params } }) => ({ token, params }), shallowEqual);
    const { isClickInnerCard } = useAppSelector(({ controller: { isClickInnerCard } }) => ({ isClickInnerCard }), shallowEqual);

    const [requiredsItems, setRequiredsItems] = useState<string[]>([]);
    const [notRequiredsItems, setNotRequiredsItems] = useState<string[]>([]);
    let queryParams: IParams = {};

    const handleTokenParams = async () => {
        if (query.isAuth === "true") {
            if (token) dispatch(setIsReady(true));
        } else {
            dispatch(setIsReady(true));
        }
    };

    const handleSetParamsInRedux = () => dispatch(setPlayerParams(queryParams));

    const handleTheme = () => {
        if (query.theme) {
            const theme = query.theme === "light" ? "light" : "dark";
            setTheme(theme);
            queryParams.theme = theme;
        } else {
            if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
                setTheme("dark");
                queryParams.theme = "dark";
            } else {
                setTheme("light");
                queryParams.theme = "light";
            }
        }
    };

    const handleFeatureSet = () => {
        let featureSet = queryParams.featureSet;
        if (typeof queryParams.featureSet === "string") featureSet = strToObj(queryParams.featureSet);
        queryParams.featureSet = featureSet;
        return queryParams;
    };

    const handleAddUUID = () => {
        if (!!query.uuid) {
            localStorage.setItem("hamrahiUUID", query.uuid!);
            queryParams.uuid = query.uuid;
        }
    };

    const handleXToken = () => {
        const xTokenInCookie = Cookies.get("xToken");
        if (xTokenInCookie && !queryParams.xToken) queryParams.xToken = xTokenInCookie;
    };

    const handleLiveAndVodParams = () => {
        queryParams = {
            portrait:
                conditions(ConditionName.isTouchScreen) && !conditions(ConditionName.isSplusEnvironment) && query.isGeneral !== "true"
                    ? "true"
                    : "false",
            provider: "unknown",
            chart: "true",
            authentication: "true",
            ...query
        };
        dispatch(setIsComplexDesktop(queryParams.portrait === "true" && !conditions(ConditionName.isTouchScreen)));
        dispatch(setIsComplexPortrait(handleIsComplexPortrait({ portrait: queryParams.portrait, ...query })));
    };

    const handleStaticParams = () => {
        const eUrl = String(query["e-url"]);
        const eCover = String(query["e-cover"]);
        queryParams = {
            src: eUrl.endsWith("/") ? window.atob(eUrl.slice(0, -1)) : window.atob(eUrl),
            type: query.type,
            fullscreen: query.fullscreen,
            autoplay: query.autoplay,
            snapshot: query.snapshot,
            portrait: "false",
            fullrate: query.fullrate,
            width: query.width,
            height: query.height,
            cover: !!eCover && eCover !== "undefined" ? window.atob(eCover) : undefined,
            onFinished: query.onFinished,
            onSnapshot: query.onSnapshot,
            uuid: getUUID()
        };
    };

    const checkRequireds = (query: IParams, requireds: string[], optionals: string[]) => {
        const queryParamsKeys = Object.keys(query);
        let requiredsItems = findIntersectionBetweenTwoArray(requireds, queryParamsKeys);
        let notRequiredsItems = queryParamsKeys.filter((item) =>
            findIntersectionBetweenTwoArray(findIntersectionBetweenTwoArray(allParams, requireds), optionals).includes(item)
        );
        return {
            requiredsItems,
            notRequiredsItems
        };
    };

    const checkParamsConditions = () => {
        let requiredsCondtions: string[] = [];
        let optionalsConditions: string[] = [];

        if (query.quiz === "true") {
            requiredsCondtions = quizConditions.requireds;
            optionalsConditions = quizConditions.optionals;
        } else if (query.raw === "true") {
            requiredsCondtions = rawConditions.requireds;
            optionalsConditions = rawConditions.optionals;
        } else if (query.isGeneral === "true") {
            requiredsCondtions = isGeneralConditions.requireds;
            optionalsConditions = isGeneralConditions.optionals;
        } else if (query.type === "mp4") {
            requiredsCondtions = staticConditions.requireds;
            optionalsConditions = staticConditions.optionals;
        } else if (["live", "vod", "archive", "clip"].includes(String(query.type))) {
            requiredsCondtions = nonStaticConditions.requireds;
            optionalsConditions = nonStaticConditions.optionals;
        }

        return {
            requiredsCondtions,
            optionalsConditions
        };
    };

    const handleParams = async () => {
        queryParams = {};
        if (Object.keys(query).includes("type")) {
            const { requiredsItems, notRequiredsItems } = checkRequireds(
                query,
                checkParamsConditions().requiredsCondtions,
                checkParamsConditions().optionalsConditions
            );
            setNotRequiredsItems(notRequiredsItems);
            if (requiredsItems.length) setRequiredsItems(requiredsItems);
            else {
                if (["live", "vod", "archive", "clip"].includes(String(query.type))) {
                    handleLiveAndVodParams();
                    handleXToken();
                } else if (staticFormats(String(query.type))) handleStaticParams();
                handleAddUUID();
                if (Object.keys(queryParams)) handleFeatureSet();
                handleTheme();
                handleSetParamsInRedux();
                handleTokenParams();
            }
        } else setRequiredsItems(["type"]);
    };

    useLayoutEffect(() => {
        if (params.id && query.quiz !== "true" && isClickInnerCard) {
            query = { ...params, homeButtonClickFlag: query.homeButtonClickFlag };
            handleParams();
        }
    }, [params.id]);

    useLayoutEffect(() => {
        handleParams();
    }, [query, token, isClickInnerCard]);

    return {
        requiredsItems,
        notRequiredsItems
    };
};

export default useHandleParams;
